import React from 'react';
import {
    Alert,
    Badge,
    Breadcrumb,
    Button,
    Card,
    Col,
    Dropdown,
    Icon,
    Menu,
    message, Modal,
    Popconfirm,
    Row,
    Spin,
    Table,
    Tooltip
} from 'antd';
import gql from 'graphql-tag';
import LoadingContent from '../../../LoadingContent';
import {graphql} from 'react-apollo';
import compose from 'lodash.flowright';
import {Link, withRouter} from 'react-router-dom';
import commonColumns from '../../../../common/table/columns';
import WithOrgData from '../../../WithOrgData';
import pluralize from 'pluralize';
import {withUserGroups} from '../../../userGroup';
import withBoardingRestrictionDrawer from 'components/withBoardingRestrictionDrawer';
import ETable from 'components/enchanced-antd-table';
import LiveColoredButton from 'components/live-2/LiveColoredButton';
import ColorKey from 'components/live-2/ColorKey';
import {PassengerEditorModal} from "../../../PassengerEditor/PassengerEditorModal";
import {
    PaxNodeComplianceAuthorizationFieldsFragment,
    PaxNodeComplianceFieldsFragment
} from "../../../../Queries/Compliance";
import {IsnResultWithHeaderFragment} from "../../../../Queries/Isn";
import {withComplianceManagerDrawer} from "../../../compliance/compliance-manager/drawer";
import {BulkComplianceCheckerContext} from "../../../../context/compliance/bulk-compliance-checker";
import useBulkComplianceChecker from "../../../../hooks/compliance/bulk-compliance-checker";
import ComplianceStatusButton from "../../../compliance/compliance-status-button";
import {BrdRestrictionStatus} from "../../../../__generated__/graphql/types";
import moment from "moment";

const FINALIZE = gql`
mutation FinalizeBoat($_id: ID! $tpID: ID!){
    finalizeBoat(_id: $_id tpID: $tpID){
        _id
        dockState
        state
        paxIDList {
            _id
            nodeState
        }
        cgoIDList {
            _id
            nodeState
            approvedBy
        }
    }
}
`

const BOAT = gql`
query Boat($_id: ID!){
    getBoat(_id: $_id){
        _id
        desig
        boatCrewIDs {
            _id
            __typename
            contactInfo {
                name {
                    firstName
                    lastName
                }
            }
            locationID {
                _id
                name
            }
            authorization {
                onWhiteList
                ...PaxNodeComplianceAuthorizationFieldsFragment,
                brdRestriction_Illness {
                    status
                    type
                    notes
                    reason
                }
                brdRestriction_Quarantined {
                    status
                    type
                    notes
                    reason
                }
            }
            lastIsnResultWithHeader {
                ...IsnResultWithHeaderFragment
            }
        }
        paxIDList {
            _id
            __typename
            employerID {
                _id
                name
            }
            departureID {
                _id
                name
            }
            destinationID {
                _id
                name
            }
            personID {
                _id
                dob
                nation
            }
            paxWeight
            bagWeight
            bagCount
            lastName
            firstName
            transitType
            checkedInTime
            checkInType
            authorization {
                onWhiteList
                ...PaxNodeComplianceAuthorizationFieldsFragment,
                brdRestriction_Illness {
                    status
                    type
                    notes
                    reason
                }
                brdRestriction_Quarantined {
                    status
                    type
                    notes
                    reason
                }
            }
            ...PaxNodeComplianceFieldsFragment
        }
        destinationIDs {
            _id
            name
            type
            block
            field
            ownerOrg
            modifiedTs
        }
        departureIDs {
            _id
            name
            type
            block
            field
            ownerOrg
            modifiedTs
        }
    }
}
${PaxNodeComplianceAuthorizationFieldsFragment}
${PaxNodeComplianceFieldsFragment}
${IsnResultWithHeaderFragment}
`

const REMOVE_PAX = gql`
mutation RemPaxFromBoat(
    $boatID: ID!
    $paxIDs: [ID]!
    $tpID: ID!
){
    removePassengersFromBoat(
        boatID: $boatID
        paxIDs: $paxIDs
        tpID: $tpID
    ) {
        _id
        paxIDList {
            _id
        }
    }
}
`

const MUTATION_MAKE_PAX_ONSITE = gql`
mutation MakePaxOnSite($paxID: ID!){
    ChangePaxCheckInType(paxID: $paxID, newCheckInType: "WEBDOCKS"){
        _id
        checkInType
    }
}
`

class BoatDetail_Internal extends React.Component {
    state = {
        tabKey: 'pax',
        finalizing: false,
        selectedPax: [],
        makingPaxOnSite: null
    }

    getPaxComplianceResult = (paxID) => {
        return this.props.data.getBoat.paxIDList.find((pax) => pax._id === paxID)?.lastComplianceEngineResult
    }
    componentDidMount(){
        this.props.data.startPolling(5000)
    }

    // Validate pax before finalizing
    validatePax = () => {
        if (!this.props.data || !this.props.data.getBoat){
            return null;
        }

        const { getBoat: { paxIDList } } = this.props.data;

        for (const pax of paxIDList){
            const result = this.getPaxComplianceResult(pax?._id);
            if (!result){
                return {
                    valid: false,
                    reason: 'One or more passengers is missing a compliance result. Please run the compliance check.'
                }
            }
            if (!result.success && pax.authorization?.brdRestriction_Compliance?.status !== BrdRestrictionStatus.OVERRIDDEN){
                return {
                    valid: false,
                    reason: 'One or more passengers has a bad compliance status. All passengers with a bad compliance status must be overridden first.'
                };
            }
        }

        return {
            valid: true
        };
    }

    latestComplianceCheckTime = () => {
        if (!this.props.data || !this.props.data.getBoat){
            return null;
        }

        const { getBoat: { paxIDList } } = this.props.data;

        let latestTime = null;

        for (const pax of paxIDList){
            const result = this.getPaxComplianceResult(pax?._id);

            if (!result) continue;

            if (result.startTs && result.startTs > latestTime || !latestTime){
                latestTime = result.startTs;
            }
        }

        return latestTime ? moment(latestTime) : null;
    }

    checkAndConfirmComplianceCheck = async () => {
        const threshold = 24; // hours
        const latestCheckTime = this.latestComplianceCheckTime();

        const now = moment();

        const diff = latestCheckTime ? now.diff(latestCheckTime, 'hours') : null;

        if (diff && diff < threshold){
            return true; // Recent compliance check exists
        }

        return new Promise((resolve) => {
            Modal.confirm({
                title: 'Compliance Check Required',
                content: `The latest compliance check was performed over ${threshold} hours ago. It is recommended to run a fresh compliance check before finalizing the boat. Do you want to run the compliance check now?`,
                okText: 'Run Compliance Check and Finalize',
                cancelText: 'Skip',
                onOk: async () => {
                    await this.handleComplianceCheck();
                    resolve(true);
                },
                onCancel: () => {
                    resolve(true); // User chose to skip
                }
            });
        });
    }

    finalize = async () => {
        const { mutate, history } = this.props;
        this.setState({ finalizing: true })

        // Run optional compliance check before finalizing
        await this.checkAndConfirmComplianceCheck();

        // Then validate results
        const paxValResult = this.validatePax();
        if (!paxValResult.valid){
            message.error(paxValResult.reason, 10);
            return;
        }

        mutate()
        .then(() => {
            message.success("Boat successfully finalized")
            history.push('/app/boats')
        })
        .catch(error => {
            message.error("Failed to finalize boat")
        })
        .then(() => this.setState({ finalizing: false }))
    }
    handleComplianceCheck = async () => {
        const { getBoat: { paxIDList } } = this.props.data;
        this.props.data.stopPolling()

        await this.props.bulkComplianceChecker.begin(paxIDList?.map(pax => pax._id) || []);
        
        this.props.data.startPolling(5000)
    }
    renderDesig = () => {
        const { data: { getBoat, loading } } = this.props;
        return (loading && !getBoat) ? <span><Spin /> Loading</span> : getBoat && getBoat.desig
    }
    removePassengers = () => {
        const { removePassengers } = this.props;
        const { data: { getBoat: boat } } = this.props;
        const { selectedPax } = this.state;
        removePassengers({
            variables: {
                paxIDs: this.state.selectedPax,
                boatID: this.props.match.params.boatid,
                tpID: this.props.orgData.transporter._id
            },
            optimisticResponse: {
                __typename: 'Mutation',
                removePassengersFromBoat: {
                    __typename: 'BoatNode',
                    _id: boat._id,
                    paxIDList: boat.paxIDList.filter(pax => !selectedPax.includes(pax._id))
                }
            }
        })
        .then(() => {
            message.success('Boat successfully updated');
            this.setState({ selectedPax: [] })
        })
        .catch(() => message.error('Failed to update boat due to error'))
    }
    makePaxOnSite = (paxID) => {
        const { makePaxOnSite } = this.props;
        this.setState({ makingPaxOnSite: paxID });

        makePaxOnSite({
            variables: {
                paxID: paxID
            }
        })
        .then(() => {
            this.setState({ makingPaxOnSite: null });
            message.success("Passenger made on-site successfully.");
        })
        .catch(() => {
            this.setState({ makingPaxOnSite: null });
            message.error("Failed to make passenger on-site due to an error.")
        })
    }
    renderContent = () => {
        const { data: { getBoat, loading, error }, userGroups } = this.props;
        if (loading) return <LoadingContent />
        if (error) return <Alert type="error" message="Failed to load boat" description={error.message} showIcon />
        const destLen = getBoat.destinationIDs ? getBoat.destinationIDs.length : 0;
        const departLen = getBoat.departureIDs ? getBoat.departureIDs.length : 0;
        const paxLen = getBoat.paxIDList ? getBoat.paxIDList.length : 0;
        const crewLen = getBoat.boatCrewIDs ? getBoat.boatCrewIDs.length : 0;
        const renderCrew = (crewMembers) => (
            <Table
                dataSource={crewMembers}
                rowKey={record => record._id}
                size="small"
                className="mc-table"
                pagination={false}
                columns={[
                    ...commonColumns.crew.some(['name', 'location'])
                ]}
            />
        )
        const renderPassengers = (passengers) => {
            const canRemovePax = userGroups.includes('flytsuite.docks.removepassenger');
            const restriction = commonColumns.pax.columns.restrictions;
            return (
                <div>
                    <div style={{ marginBottom: 12, display: 'flex' }}>
                        {canRemovePax ? (
                            <Popconfirm
                            title={
                                <span>
                                    Are you sure you want to remove these passengers from this boat? This action <strong>cannot</strong> be undone!
                                </span>
                            }
                            onConfirm={() => this.removePassengers()}
                            >
                                <Button
                                    disabled={!canRemovePax || !this.state.selectedPax.length}
                                    type="danger"
                                >
                                    {this.state.selectedPax.length ? 
                                        `Remove ${this.state.selectedPax.length} ${pluralize('Passenger', this.state.selectedPax.length)}` :
                                        'Remove Passengers'
                                    }
                                </Button>
                            </Popconfirm>
                        ) : (
                            <span style={{ marginLeft: 12, color: 'red' }}>
                                You do not have permission to remove passengers
                            </span>
                        )}
                        <Dropdown
                            overlay={<Menu>
                                <div style={{ padding: '6px 12px' }}>
                                    <h4>Pax Name Color Legend</h4>
                                    <ColorKey type="overnight" text="Overnight" />
                                    <ColorKey type="expected" text="Expected" />
                                </div>
                            </Menu>
                                
                            }
                        >
                            <Button style={{ marginLeft: 'auto' }}>Legend <Icon type="down" /></Button>
                        </Dropdown>
                    </div>
                    <ETable
                        dataSource={passengers}
                        rowKey={record => record._id}
                        size="small"
                        className="mc-table mc-table-scroll-auto mc-table-header-nowrap"
                        style={{ maxWidth: '100%' }}
                        flexMode
                        bordered
                        pagination={false}
                        rowSelection={ canRemovePax && {
                            selectedRowKeys: this.state.selectedPax,
                            onChange: (keys) => this.setState({ selectedPax: keys })
                        }}
                        columns={[
                            {
                                ...commonColumns.pax.columns.name,
                                render: (_, record) => (
                                    <Tooltip
                                        title={"Passenger's Check In Type is " + record.checkInType}
                                        placement="right"
                                        mouseEnterDelay={1}>
                                        <LiveColoredButton
                                            type={record.checkInType}
                                            className="mc-link-btn"
                                            onClick={() => this.props.paxEditorModalState.open(record._id)}
                                        >
                                            {`${record.lastName}, ${record.firstName}`}
                                        </LiveColoredButton>
                                    </Tooltip>
                                )
                            },
                            ...commonColumns.pax.some(['employer', 'weight', 'bagWeight', 'bagCount', 'checkedIn', 'departure', 'destination']),
                            {
                                ...commonColumns.pax.columns.restrictions,
                                render: (text, record, index) => (
                                    restriction.render(text, record, index, () => this.props.boardingRestrictionsDrawer.open(record._id))
                                )
                            },
                            {
                                ...commonColumns.pax.columns.compliance,
                                render: (text, record) => {
                                    if (record.transitType !== 'INBOUND'){
                                        return (
                                            <ComplianceStatusButton pax={record} size="small" block />
                                        )
                                    }
                                    return null
                                }
                            },
                            {
                                title: 'Actions',
                                key: 'actions',
                                width: 40,
                                render: (_, record) => {
                                    if (record.checkInType === 'EXPECTED'){
                                        return <Tooltip title='Change passenger from expected to on-site'>
                                            <Popconfirm
                                                title="Are you sure you want to make this passenger on-site? THIS OPERATION CANNOT BE UNDONE!"
                                                onConfirm={() => this.makePaxOnSite(record._id)}
                                                >
                                                <Button
                                                    size='small'
                                                    type='primary'
                                                    loading={this.state.makingPaxOnSite === record._id}
                                                    disabled={this.state.makingPaxOnSite !== null && this.state.makingPaxOnSite !== record._id}
                                                >Make On-Site</Button>
                                            </Popconfirm>
                                            
                                        </Tooltip>
                                    }
                                }
                            }
                        ]}
                    />
                </div>
            )
        }
        const renderLocations = (locations) => (
            <Table
                dataSource={locations}
                rowKey={record => record._id}
                size="small"
                className="mc-table"
                pagination={false}
                columns={commonColumns.location.all()}
            />
        )
        const tabContent = {
            depart: renderLocations(getBoat.departureIDs),
            dest: renderLocations(getBoat.destinationIDs),
            pax: renderPassengers(getBoat.paxIDList),
            crew: renderCrew(getBoat.boatCrewIDs)
        }
        const Count = (props) => <Badge {...props} style={{
            marginBottom: '-0.25rem',
            marginLeft: '0.4rem',
            background: '#1890ff'
        }} />
        return (
            <Card
                tabList={[
                    {
                        key: 'pax',
                        tab: (
                            <div>
                                <span>Passengers</span>
                                <Count count={paxLen} />
                            </div>
                        )
                    },
                    {
                        key: 'crew',
                        tab: (
                            <div>
                                <span>Crew Members</span>
                                <Count count={crewLen} />
                            </div>
                        )
                    },
                    {
                        key: 'depart',
                        tab: (
                            <div>
                                <span>Departures</span>
                                <Count count={departLen} />
                            </div>
                        )
                    },
                    {
                        key: 'dest',
                        tab: (
                            <div>
                                <span>Destinations</span>
                                <Count count={destLen} />
                            </div>
                        )
                    }
                ]}
                onTabChange={(tabKey) => this.setState({ tabKey })}
                activeTabKey={this.state.tabKey}
            >
            {tabContent[this.state.tabKey]}
            </Card>
        )
    }
    render(){
        let cannotFinalizeReason = null;

        const paxValResult = this.validatePax();

        if (paxValResult && !paxValResult.valid){
            cannotFinalizeReason = paxValResult.reason;
        }

        console.debug(paxValResult, cannotFinalizeReason);

        return (
            <div style={{ height: '100%', overflowX: 'auto', padding: '12px' }}>
                <div style={{  minWidth: '600px' }}>
                    <Breadcrumb>
                        <Breadcrumb.Item>
                            <Link to="/app/boats" >Live Boats</Link>
                        </Breadcrumb.Item>
                        <Breadcrumb.Item>
                            {this.renderDesig()}
                        </Breadcrumb.Item>
                    </Breadcrumb>
                    <Row style={{margin: '0.5rem 0 1rem 0'}}>
                        <Col span={12}>
                            <h2 style={{margin: 0}}>{this.renderDesig()}</h2>
                        </Col>
                        <Col span={12}>
                            <Row type="flex" justify="end" gutter={12}>
                                {this.state.tabKey === 'pax' ? (
                                    <Col>
                                        <Button loading={this.props.bulkComplianceChecker.isRunning} onClick={this.handleComplianceCheck} >
                                            Run Compliance Check
                                        </Button>
                                    </Col>
                                ) : null}
                                <Col>
                                    <Popconfirm disabled={Boolean(cannotFinalizeReason)} okText="Finalize" title="Are you sure you want to finalize this boat?" placement="bottomLeft" onConfirm={this.finalize}>
                                        <Button
                                            disabled={Boolean(cannotFinalizeReason)}
                                            loading={this.state.finalizing}
                                            type="primary"
                                        >Finalize</Button>
                                    </Popconfirm>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                    {cannotFinalizeReason ? (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBlock: '1rem' }}
                            message="Finalize disabled"
                            description={
                                <div>
                                    <p>{cannotFinalizeReason}</p>
                                    <Button
                                        type="primary"
                                        onClick={this.handleComplianceCheck}
                                        loading={this.props.bulkComplianceChecker.isRunning}
                                    >Re-run Compliance Check</Button>
                                </div>
                            }
                        />
                    ) : null}
                    <div>
                        {this.renderContent()}
                    </div>
                </div>
                <PassengerEditorModal
                    api={this.props.paxEditorModalState}
                    edit={true}
                    onPassengerUpdated={() => this.props.data.refetch()}
                    onPassengerDeleted={() => this.props.data.refetch()}
                />
            </div>
        )
    }
}

function BoatDetail(props){

    const paxEditorModalState = PassengerEditorModal.useState();
    const bulkComplianceChecker = useBulkComplianceChecker({
        onCompleted: () => props.data.refetch()
    });

    return (
        <BulkComplianceCheckerContext.Provider value={bulkComplianceChecker}>
            <BoatDetail_Internal
                {...props}
                paxEditorModalState={paxEditorModalState}
                bulkComplianceChecker={bulkComplianceChecker}
            />
        </BulkComplianceCheckerContext.Provider>
    )
}

const BoatDetailWithData = compose(
    withRouter,
    WithOrgData,
    withUserGroups,
    withBoardingRestrictionDrawer,
    graphql(
        BOAT,
        {
            options: props => {
                return {
                    variables: {
                        _id: props.match.params.boatid
                    },
                    fetchPolicy: 'network-only'
                }
            }
        }
    ),
    graphql(
        FINALIZE,
        {
            options: props => {
                return {
                    variables: {
                        _id: props.match.params.boatid,
                        tpID: props.orgData.transporter._id
                    }
                }
            }
        }
    ),
    graphql(
        REMOVE_PAX,
        {
            name: 'removePassengers'
        }
    ),
    graphql(
        MUTATION_MAKE_PAX_ONSITE,
        {
            name: 'makePaxOnSite'
        }
    ),
    withComplianceManagerDrawer
)(BoatDetail)

export default BoatDetailWithData